import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import { API_URL } from '../services/api';

interface Notification {
  id: string;
  type: 'deposit_confirmed' | 'balance_confirmed' | 'status_updated' | 'order_created' | 'payment_confirmed' | 'order_cancelled' | 'balance_payment_pending';
  title: string;
  message: string;
  orderId?: string;
  status?: string;
  timestamp: Date;
  read: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode; userId?: string; isAdmin?: boolean }> = ({ children, userId, isAdmin = false }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  // Save to localStorage whenever notifications change
  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem('notifications', JSON.stringify(notifications));
      console.log('💾 Saved notifications to localStorage');
    } else {
      localStorage.removeItem('notifications');
    }
  }, [notifications]);
  
  // Load saved notifications from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setNotifications(parsed.map((n: any) => ({ 
          ...n, 
          timestamp: new Date(n.timestamp) 
        })));
        console.log('📦 Loaded notifications from localStorage');
      } catch (e) {
        console.error('Failed to parse notifications:', e);
      }
    }
  }, []);
  
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      read: false
    };

    setNotifications(prev => {
      const isDuplicate = prev.some(item =>
        item.type === newNotification.type &&
        item.title === newNotification.title &&
        item.message === newNotification.message &&
        item.orderId === newNotification.orderId &&
        Math.abs(item.timestamp.getTime() - newNotification.timestamp.getTime()) < 1000
      );

      return isDuplicate ? prev : [newNotification, ...prev];
    });
  }, []);
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    const socketUrl = API_URL.replace('/api', '');
    console.log('🔌 Connecting to socket at:', socketUrl);
    
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      query: {
        token: token || '',
        role: isAdmin ? 'admin' : 'user'
      }
    });
    
    socket.on('connect', () => {
      console.log('🔌 Socket connected successfully, userId:', userId);
      if (userId) {
        socket.emit('register-user', userId);
        console.log('📡 Registered user with socket:', userId);
      }
    });
    
    const handleIncomingNotification = (notification: any, source: string) => {
      console.log(`🔔 Received ${source} notification:`, notification);
      addNotification({
        type: notification.type,
        title: notification.title,
        message: notification.message,
        orderId: notification.orderId,
        status: notification.status,
        timestamp: new Date(notification.timestamp || Date.now())
      });

      window.dispatchEvent(new CustomEvent('new-notification', { detail: notification }));
    };

    socket.on('notification', (notification: any) => {
      handleIncomingNotification(notification, 'notification');
    });

    socket.on('order_status_updated', (event: any) => {
      const notification = {
        type: 'status_updated' as const,
        title: `Order Update ${event.status ? `(${event.status})` : ''}`,
        message: `Your order ${event.orderReference || event.orderId?.slice(-8) || 'details'} has been updated to ${event.status || 'a new status'}.`,
        orderId: event.orderId,
        status: event.status,
        timestamp: new Date()
      };

      addNotification(notification);
      window.dispatchEvent(new CustomEvent('new-notification', { detail: notification }));
    });

    socket.on('admin_notification', (notification: any) => {
      handleIncomingNotification(notification, 'admin_notification');
    });
    
    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });
    
    return () => {
      socket.disconnect();
    };
  }, [userId, isAdmin, addNotification]);
  
  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };
  
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
  };
  
  const clearNotifications = () => {
    setNotifications([]);
  };
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};